"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UWSAdapter = void 0;
const helperFunctions_1 = require("./utility/helperFunctions");
const Adapter_1 = require("./Adapter");
const ParserService_1 = require("./ParserService");
const decompress_1 = require("./utility/decompress");
const Types_1 = require("./Types");
let retryId;
class UWSAdapter extends Adapter_1.Adapter {
    constructor(options, uwsOptions) {
        super(options);
        this.uwsOptions = {
            retryInterval: 1000,
            maxRetryInterval: 10000,
            maxRetryAttempts: null, // If set to null, it will always try to reconnect
            room: 'events',
            grantCode: null,
            randomizationFactor: 500,
        };
        this.retryAttempts = 0;
        this.channels = [];
        this.isManuallyDisconnected = false;
        // maxMessageDelay : default value for other messages is 15 000
        this.maxMessageDelay = {
            step: 5000,
            crash: 5000,
            upcomingEvents: 5000,
            idle: 5000,
            start: 20000,
        };
        if ((0, helperFunctions_1.isNil)(options.productId)) {
            throw new Error(Types_1.BusErrors.MissingProductIdError);
        }
        this.uwsOptions = { ...this.uwsOptions, ...uwsOptions };
        this.setName();
    }
    getChannelNamespace() {
        let namespace = 'NSoft';
        if (this.uwsOptions.usePlatformNamespace && this.options.platformName) {
            namespace = `${namespace}.${(0, helperFunctions_1.upperFirst)(this.options.platformName)}`;
        }
        return namespace;
    }
    onOpenCallback(event) {
        clearTimeout(retryId);
        this.retryAttempts = 0;
        this.checkMessageDelay(event.type);
        (0, ParserService_1.parseAndDispatchEvent)('connectionSuccess', {
            subscriptionId: this.options.productId
        }, this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
    }
    onMessageCallback(event) {
        clearTimeout(this.messageDelayId);
        const reader = new FileReader();
        try {
            reader.readAsArrayBuffer(event.data);
            reader.onload = () => {
                const result = reader.result;
                const data = (0, decompress_1.decompress)(result);
                Object.assign(data, { subscriptionId: this.options.productId });
                this.checkMessageDelay(data.event);
                (0, ParserService_1.parseAndDispatchEvent)(data === null || data === void 0 ? void 0 : data.event, data, this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
            };
        }
        catch (_a) {
            let data;
            try {
                data = JSON.parse(event.data);
            }
            catch (_b) {
                data = event.data;
            }
            Object.assign(data, { subscriptionId: this.options.productId });
            this.checkMessageDelay(data.event);
            (0, ParserService_1.parseAndDispatchEvent)(data === null || data === void 0 ? void 0 : data.event, data, this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
        }
    }
    onCloseCallback() {
        this.isManuallyDisconnected = false;
    }
    checkMessageDelay(eventName) {
        var _a;
        const delay = (_a = this.maxMessageDelay[eventName]) !== null && _a !== void 0 ? _a : 15000;
        this.messageDelayId = setTimeout(() => {
            this.retryConnection();
        }, delay);
    }
    retryConnection() {
        const { retryInterval, maxRetryInterval, maxRetryAttempts, randomizationFactor } = this.uwsOptions;
        let exponentialTimeout = 0;
        if (!this.isManuallyDisconnected) {
            const startInterval = () => {
                retryId = setTimeout(() => {
                    clearTimeout(retryId);
                    const randomDelay = Math.round(((Math.random() * 2) - 0.5) * randomizationFactor);
                    this.retryAttempts++;
                    exponentialTimeout = retryInterval * Math.pow(2, this.retryAttempts) + randomDelay;
                    //@ts-expect-error MaxRetryAttempts is already checked for beeing null
                    if (!(0, helperFunctions_1.isNil)(maxRetryAttempts) && this.retryAttempts > maxRetryAttempts) {
                        (0, ParserService_1.parseAndDispatchEvent)('connectionLost', {
                            subscriptionId: this.options.productId
                        }, this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
                        return;
                    }
                    (0, ParserService_1.parseAndDispatchEvent)('connectionReconnect', {
                        subscriptionId: this.options.productId
                    }, this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
                    startInterval();
                }, exponentialTimeout > maxRetryInterval ? maxRetryInterval : exponentialTimeout);
            };
            startInterval();
        }
    }
    setListeners() {
        var _a, _b, _c;
        (_a = this.instance) === null || _a === void 0 ? void 0 : _a.addEventListener('open', this.onOpenCallback.bind(this));
        (_b = this.instance) === null || _b === void 0 ? void 0 : _b.addEventListener('message', this.onMessageCallback.bind(this));
        (_c = this.instance) === null || _c === void 0 ? void 0 : _c.addEventListener('close', this.onCloseCallback.bind(this));
    }
    removeListeners() {
        var _a, _b, _c;
        (_a = this.instance) === null || _a === void 0 ? void 0 : _a.removeEventListener('open', this.onOpenCallback.bind(this));
        (_b = this.instance) === null || _b === void 0 ? void 0 : _b.removeEventListener('message', this.onMessageCallback.bind(this));
        (_c = this.instance) === null || _c === void 0 ? void 0 : _c.removeEventListener('close', this.onCloseCallback.bind(this));
    }
    subscribe() {
        this.setListeners();
    }
    unsubscribe() {
        this.removeListeners();
    }
    createWS() {
        if (!(0, helperFunctions_1.isNil)(this.instance)) {
            this.unsubscribe();
            this.instance = undefined;
        }
        const grantCode = !(0, helperFunctions_1.isNil)(this.uwsOptions.grantCode) ? [this.uwsOptions.grantCode] : [];
        //@ts-expect-error TODO grant code can be null
        this.instance = new WebSocket(this.getEndpoint(), grantCode);
        this.subscribe();
    }
    getEndpoint() {
        // TODO: This should be upgraded for UWS to be the universal and not Crash Cash only adapter
        switch (this.options.environment) {
            case 'production':
                return 'wss://crash-cash-websocket.de-2.nsoft.cloud';
            default:
                return 'wss://crash-cash-websocket.staging.de-2.nsoft.cloud';
        }
    }
    addChannels() {
        this.channels.forEach((channel) => {
            this.subscribeChannel(channel);
        });
    }
    addChannel(channelType, channelName) {
        const optionalChannelName = !(0, helperFunctions_1.isNil)(channelName) ? `.${channelName}` : '';
        const channelId = `${this.getChannelNamespace()}.${channelType}${optionalChannelName}`;
        if (this.channels.findIndex((n) => n === channelId) < 0) {
            this.channels.push(channelId);
            this.subscribeChannel(channelId);
        }
    }
    subscribeChannel(channelId) {
        const subscriptionMessage = {
            action: 'subscribe',
            data: {
                channels: [channelId],
            }
        };
        this.sendMessage(subscriptionMessage);
    }
    removeChannel(channelType, channelName) {
        const channelId = `${this.getChannelNamespace()}.${channelType}.${channelName}`;
        const channelIndex = this.channels.findIndex((n) => n === channelId);
        if (channelIndex >= 0) {
            this.channels.splice(channelIndex, 1);
            // this.unsubscribe(channelId);
        }
        // console.warn(`${this.name}: ${BusErrors.MethodNotImplementedError}`, channelName);
    }
    setName() {
        this.name = Types_1.Adapters.UWSAdapter;
    }
    sendMessage(message) {
        var _a;
        (_a = this.instance) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(message));
    }
    connect() {
        this.createWS();
    }
    reconnect() {
        var _a;
        if (((_a = this.instance) === null || _a === void 0 ? void 0 : _a.readyState) === Types_1.WSStates.CLOSED) {
            this.connect();
        }
    }
    disconnect(message) {
        var _a;
        this.unsubscribe();
        (_a = this.instance) === null || _a === void 0 ? void 0 : _a.close(1000, message);
        this.instance = undefined;
        this.isManuallyDisconnected = true;
        clearTimeout(this.messageDelayId);
    }
    async getState() {
        console.warn(`${this.name}: ${Types_1.BusErrors.MethodNotImplementedError}`);
    }
}
exports.UWSAdapter = UWSAdapter;
//# sourceMappingURL=UWSAdapter.js.map